import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import store from 'store';
import {
  checkUserToken,
  logUserOut,
  redirectToLogin,
  refreshUserToken,
} from '../actions/authentication';
import { getInstalls } from '../actions/customerPortal';
import { dismissError, dismissSuccess } from '../actions/common';
import { storeWithExpiration, checkCPUser, checkCPAdmin } from '../utils/common';
import {
  APP_TOKEN_POLL,
  APP_SESSION_TIMEOUT,
  APP_TOKEN_REFRESH_TIME,
  SUPER_ADMIN,
} from '../config/app_config';

// Components
import Template from './Template';

export class TemplateContainer extends React.Component {
  static propTypes = {
    accountSettings: PropTypes.object.isRequired,
    authentication: PropTypes.object.isRequired,
    checkTokenAction: PropTypes.func.isRequired,
    children: PropTypes.node,
    dismissErrorAction: PropTypes.func.isRequired,
    dismissSuccessAction: PropTypes.func.isRequired,
    error: PropTypes.object,
    getInstallsAction: PropTypes.func.isRequired,
    installations: PropTypes.object,
    logoutAction: PropTypes.func.isRequired,
    params: PropTypes.object.isRequired,
    pathname: PropTypes.string.isRequired,
    progress: PropTypes.number.isRequired,
    redirectToLoginAction: PropTypes.func.isRequired,
    refreshTokenAction: PropTypes.func.isRequired,
    success: PropTypes.object,
    user: PropTypes.object.isRequired,
  };

  static defaultProps = {
    children: null,
    error: null,
    installations: null,
    success: null,
  };

  state = {
    showSessionWarning: false,
    isLoaded: true,
  };

  componentWillMount = () => {
    const { checkTokenAction, authentication, getInstallsAction, user } = this.props;
    const isCustomerPortal = checkCPUser(user.userTypeId);
    const token = storeWithExpiration.get('token');
    const { isAuthenticated } = authentication;

    // cleanup: if the token is expired, delete it from local storage
    if (!token) {
      store.clear();

      if (isAuthenticated) {
        this.redirectUserToLogin(token, isAuthenticated);
      }
    }

    // If a token exists in local storage but user isn't authed, try to auth them
    if (token && !isAuthenticated) {
      checkTokenAction();
    }

    if (isCustomerPortal && token) getInstallsAction(token);

    this.startPolling();
  };

  componentDidMount = () => {
    window.addEventListener('click', this.clickCatch, false);
  };

  componentWillReceiveProps = nextProps => {
    // Only show loading fade in if we're navigating to a new page
    const previousPathname = this.props.pathname;
    const { pathname, progress } = nextProps;
    const { isLoaded } = this.state;
    // On path change, set isLoaded to false,
    if (pathname !== previousPathname) {
      this.setState({ isLoaded: false }, () => {
        // Ugly fix for if there are no server calls on the page (aka static page)
        setTimeout(() => {
          if (!this.state.isLoaded && progress < 1) {
            this.setState({ isLoaded: true });
          }
        }, 300);
      });
    }
    // Then wait for all the promises to resolve
    if (!isLoaded && progress < 1) this.setState({ isLoaded: true });

    // If logged out (no token) redirect to login
    const { isAuthenticated } = nextProps.authentication;

    const token = storeWithExpiration.get('token');
    if (!token && isAuthenticated) {
      store.clear();
      this.redirectUserToLogin();
      // this.logUserOut();
    }

    if (isAuthenticated) {
      this.setSessionTimeout();
    }
  };

  componentWillUnmount = () => {
    if (this.intervalHandle) clearInterval(this.intervalHandle);
    if (this.sessionTimeout) clearTimeout(this.sessionTimeout);
    this.sessionTimeout = null;
    this.intervalHandle = null;
  };

  // Set the session timeout
  setSessionTimeout = () => {
    if (!this.sessionTimeout) {
      this.sessionTimeout = setTimeout(() => {
        this.setState({ showSessionWarning: true });
      }, APP_SESSION_TIMEOUT);
    }
  };

  sessionTimeout = null;
  intervalHandle = null;

  logUserOut = evt => {
    const { logoutAction } = this.props;
    evt && evt.preventDefault();
    this.setState({ showSessionWarning: false });
    logoutAction();
  };

  redirectUserToLogin = () => {
    const { redirectToLoginAction } = this.props;
    // evt && evt.preventDefault();
    if (this.intervalHandle) clearInterval(this.intervalHandle);
    if (this.sessionTimeout) clearTimeout(this.sessionTimeout);
    this.setState({ showSessionWarning: false });
    this.sessionTimeout = null;
    this.intervalHandle = null;
    redirectToLoginAction();
  };

  checkRequiresAuth = basePath => {
    switch (basePath) {
      case 'password-set':
      case 'password-reset':
      case 'forgot-password':
      case 'create-user': {
        return false;
      }
      default: {
        return true;
      }
    }
  };

  // Start polling token age
  startPolling = () => {
    if (!this.intervalHandle) {
      this.intervalHandle = setInterval(this.poll, APP_TOKEN_POLL);
    }
  };

  // Restart the session timeout whenever a user clicks
  clickCatch = () => {
    const { authentication } = this.props;
    clearTimeout(this.sessionTimeout);
    this.sessionTimeout = null;
    if (authentication.isAuthenticated) {
      this.setSessionTimeout();
      this.setState({ showSessionWarning: false });
    }
  };

  // Repeatedly check the token in localstorage
  // Refresh it when it gets close to expiring
  poll = () => {
    const { authentication, refreshTokenAction } = this.props;
    const { isAuthenticated } = authentication;
    if (isAuthenticated) {
      // get token expiration from localstorage
      const tokenExpiration = storeWithExpiration.get('tokenExpiration');
      const now = new Date().getTime();
      if (tokenExpiration - now <= APP_TOKEN_REFRESH_TIME) {
        refreshTokenAction();
      }
    }
  };

  render() {
    const {
      accountSettings,
      authentication,
      children,
      dismissErrorAction,
      dismissSuccessAction,
      error,
      installations,
      params,
      pathname,
      progress,
      success,
      user,
    } = this.props;
    const { showSessionWarning, isLoaded } = this.state;
    const { page } = params;
    const path = pathname.split('/').slice(1);
    const basePath = path[0];
    const authError = authentication.error;
    const isAdmin = user.userTypeId === SUPER_ADMIN;
    const isCustomerPortal = checkCPUser(user.userTypeId);
    const isCustomerAdmin = isCustomerPortal ? checkCPAdmin(installations) : false;
    const requiresAuth = this.checkRequiresAuth(basePath);
    const token = storeWithExpiration.get('token');
    let isAuthenticated = authentication.isAuthenticated || false;
    // If we lose the token, isAuthenticated should be passed as false;
    if (!token) {
      isAuthenticated = false;
    }

    let installObj = null;
    if (
      (page === 'configure-install' ||
        page === 'manage-users' ||
        page === 'instance-fields' ||
        page === 'privacy') &&
      params.instanceId &&
      installations &&
      installations.size > 0
    ) {
      const instanceId = parseInt(params.instanceId, 10);
      installObj = installations
        .filter(install => install.installation.get('id') === instanceId)
        .first();
    }

    return (
      <Template
        accountSettings={accountSettings}
        authError={authError}
        basePath={basePath}
        dismissErrorFunction={dismissErrorAction}
        dismissSuccessFunction={dismissSuccessAction}
        error={error}
        installObj={installObj}
        isAdmin={isAdmin}
        isAuthenticated={isAuthenticated}
        isCustomerAdmin={isCustomerAdmin}
        isCustomerPortal={isCustomerPortal}
        isLoaded={isLoaded}
        logoutFunction={this.logUserOut}
        params={params}
        path={path}
        progress={progress}
        requiresAuth={requiresAuth}
        showWarning={showSessionWarning}
        success={success}
        user={user}
      >
        {children}
      </Template>
    );
  }
}

const mapStateToProps = (state, ownProps) => {
  const {
    accountSettings,
    authentication,
    customerPortalData,
    error,
    progress,
    routing,
    success,
    user,
  } = state;
  const { params } = ownProps;
  const installations = customerPortalData.get('installations') || null;
  const { pathname } = routing.locationBeforeTransitions;
  return {
    accountSettings,
    authentication,
    error,
    installations,
    params,
    pathname,
    progress,
    success,
    user,
  };
};

export default connect(mapStateToProps, {
  checkTokenAction: checkUserToken,
  dismissErrorAction: dismissError,
  dismissSuccessAction: dismissSuccess,
  getInstallsAction: getInstalls,
  logoutAction: logUserOut,
  redirectToLoginAction: redirectToLogin,
  refreshTokenAction: refreshUserToken,
})(TemplateContainer);
